import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Github, Linkedin, Mail, Download, Sparkles, ChevronRight } from 'lucide-react';
import { ThemeToggle } from './ui/ThemeToggle';
import { Button } from './ui/Button';
import { useFocusTrap } from '../lib/useFocusTrap';
import { Logo } from './ui/Logo';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hoveredSection, setHoveredSection] = useState(null);
  const menuRef = useRef(null);
  useFocusTrap(menuRef, isMenuOpen);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 12);

      const sections = ['home', 'projects', 'opensource', 'freelance', 'experience', 'skill', 'about', 'education'];
      const scrollPos = window.scrollY + 120;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPos >= offsetTop && scrollPos < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMenuOpen]);

  const navItems = [
    { name: 'Projects', path: '#projects', id: 'projects' },
    { name: 'Open Source', path: '#opensource', id: 'opensource' },
    { name: 'Freelance', path: '#freelance', id: 'freelance' },
    { name: 'Experience', path: '#experience', id: 'experience' },
    { name: 'Skills', path: '#skill', id: 'skill' },
    { name: 'About', path: '#about', id: 'about' },
  ];

  const mobileNavItems = [
    { name: 'Home', path: '#home', id: 'home' },
    { name: 'Projects', path: '#projects', id: 'projects' },
    { name: 'Open Source', path: '#opensource', id: 'opensource' },
    { name: 'Freelance', path: '#freelance', id: 'freelance' },
    { name: 'Experience', path: '#experience', id: 'experience' },
    { name: 'Skills', path: '#skill', id: 'skill' },
    { name: 'About', path: '#about', id: 'about' },
    { name: 'Education', path: '#education', id: 'education' },
  ];

  const handleNavClick = (e, path) => {
    e.preventDefault();
    setIsMenuOpen(false);
    const section = document.querySelector(path);
    if (section) {
      const offset = section.getBoundingClientRect().top + window.scrollY - 64;
      window.scrollTo({ top: offset, behavior: 'smooth' });
    }
  };

  const socialLinks = [
    { icon: Github, href: 'https://github.com/Rakshan001', label: 'GitHub' },
    { icon: Linkedin, href: 'https://linkedin.com/in/rakshan-shetty-953864225', label: 'LinkedIn' },
    { icon: Mail, href: 'mailto:rakshanshetty2003@gmail.com', label: 'Email' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 h-16 bg-[var(--bg-canvas)] transition-all duration-200 ${
        isScrolled
          ? 'border-b border-[var(--bg-recessed)] shadow-[var(--shadow-sm)]'
          : 'border-b border-[var(--bg-recessed)]/50'
      }`}
    >
      <div className="max-w-[1200px] mx-auto px-6 h-full flex items-center justify-between">
        {/* Logo & Available Tag */}
        <a
          href="#home"
          className="flex items-center gap-3 group cursor-pointer select-none"
          onClick={(e) => handleNavClick(e, '#home')}
        >
          <div className="w-9 h-9 rounded-lg bg-[var(--bg-elevated)] border border-[var(--bg-recessed)] shadow-[var(--shadow-sm)] flex items-center justify-center text-[var(--fg-primary)] group-hover:border-[var(--fg-secondary)] transition-colors">
            <Logo size={24} className="transition-transform duration-300 group-hover:scale-110" />
          </div>
          <div className="flex flex-col">
            <span className="leading-tight text-sm font-semibold text-[var(--fg-primary)] tracking-tight">
              Rakshan Shetty
            </span>
            <span className="t-mono text-[10px] text-[var(--fg-secondary)] tracking-wider mt-0.5">
              Software Engineer
            </span>
          </div>
        </a>

        {/* Desktop Navigation with Animated Pill */}
        <nav
          className="hidden lg:flex items-center p-1 rounded-full border border-[var(--bg-recessed)] bg-[var(--bg-canvas)] shadow-[var(--shadow-sm)]"
          onMouseLeave={() => setHoveredSection(null)}
        >
          {navItems.map((item) => {
            const isActive = activeSection === item.id;
            const isHovered = hoveredSection === item.id;

            return (
              <a
                key={item.name}
                href={item.path}
                onMouseEnter={() => setHoveredSection(item.id)}
                className={`relative px-3.5 py-1 text-xs font-medium transition-colors cursor-pointer rounded-full ${
                  isActive || isHovered
                    ? 'text-[var(--fg-primary)]'
                    : 'text-[var(--fg-secondary)] hover:text-[var(--fg-primary)]'
                }`}
                onClick={(e) => handleNavClick(e, item.path)}
              >
                {/* Active sliding pill */}
                {isActive && (
                  <motion.div
                    layoutId="active-nav-pill"
                    className="absolute inset-0 rounded-full bg-[var(--bg-elevated)] border border-[var(--bg-recessed)] shadow-[var(--shadow-sm)] z-0"
                    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                  />
                )}
                <span className="relative z-10">{item.name}</span>
              </a>
            );
          })}
        </nav>

        {/* Desktop Right Actions: Social Island, Theme Toggle & Resume */}
        <div className="hidden lg:flex items-center gap-2.5">
          <div className="flex items-center p-0.5 rounded-lg border border-[var(--bg-recessed)] bg-[var(--bg-canvas)] shadow-[var(--shadow-sm)]">
            {socialLinks.map((social, index) => (
              <a
                key={index}
                href={social.href}
                className="p-1.5 rounded-md text-[var(--fg-secondary)] hover:text-[var(--fg-primary)] hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
                aria-label={social.label}
                title={social.label}
                target="_blank"
                rel="noopener noreferrer"
              >
                <social.icon size={15} />
              </a>
            ))}
          </div>

          <ThemeToggle />

          <Button
            asChild
            variant="secondary"
            size="sm"
            className="rounded-full px-3.5 text-xs font-medium"
          >
            <a href="/resume/Rakshan_Shetty_Resume.pdf" download>
              <Download size={13} className="mr-1.5" />
              Resume
            </a>
          </Button>
        </div>

        {/* Mobile Menu & Theme Toggle */}
        <div className="flex lg:hidden items-center gap-2">
          <ThemeToggle />
          <button
            type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-2 rounded-lg border border-[var(--bg-recessed)] bg-[var(--bg-elevated)] text-[var(--fg-secondary)] hover:text-[var(--fg-primary)] transition-colors cursor-pointer shadow-[var(--shadow-sm)]"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Drawer */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            ref={menuRef}
            id="mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden fixed inset-x-0 top-16 bg-[var(--bg-canvas)] shadow-[var(--shadow-lg)] border-b border-[var(--bg-recessed)] px-6 py-6 space-y-5"
          >
            <nav className="flex flex-col space-y-1">
              {mobileNavItems.map((item) => (
                <a
                  key={item.name}
                  href={item.path}
                  className={`flex items-center justify-between px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                    activeSection === item.id
                      ? 'text-[var(--fg-primary)] bg-[var(--bg-elevated)] border border-[var(--bg-recessed)] shadow-[var(--shadow-sm)]'
                      : 'text-[var(--fg-secondary)] hover:text-[var(--fg-primary)] hover:bg-[var(--bg-hover)]'
                  }`}
                  onClick={(e) => handleNavClick(e, item.path)}
                >
                  <span>{item.name}</span>
                  <ChevronRight size={14} className="text-[var(--fg-muted)]" />
                </a>
              ))}
            </nav>

            <div className="pt-4 border-t border-[var(--bg-recessed)] flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    className="p-2 rounded-lg border border-[var(--bg-recessed)] bg-[var(--bg-elevated)] text-[var(--fg-secondary)] hover:text-[var(--fg-primary)] transition-colors cursor-pointer shadow-[var(--shadow-sm)]"
                    aria-label={social.label}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <social.icon size={16} />
                  </a>
                ))}
              </div>

              <Button
                asChild
                variant="secondary"
                size="sm"
                className="rounded-full px-4 text-xs font-medium"
              >
                <a href="/resume/Rakshan_Shetty_Resume.pdf" download>
                  <Download size={13} className="mr-1.5" />
                  Resume
                </a>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}